// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// Component imports:
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DatePickerRange from '../../common/DatePickerRange';
import StickyHeaderTable from '../../common/StickyHeaderTable';
// Service imports:
import { getDatePair } from '../../../services/utils';
// Action imports:
import { requestAlertsSummaryPerTraderId } from '../../../redux-store/actions/reportActions';
// Type imports:
import { RootState } from '../../../redux-store/reducers';
import { IStatusCount } from '../../../types/api/ReportTypes';
import { exportToExcel, statusCountToString } from './helperFunctions';
import SortFilterTable from '../../common/SortFilterTable';

//@ts-ignore
window.$.DataTable = require('datatables.net')

type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps;

interface IState {
    tableData: { traderID: any; alertID: string; description: string; numAlerts: string; statusCount: IStatusCount; }[];
}

class ReportOfAlertSummaryPerTraderId extends React.Component<Props, IState> {
    private dataTableInstance: any;

    constructor(props: Props) {
        super(props);
        this.state = {
            tableData: [],
        };
        this.dataTableInstance = null;
    }

    isLoted = false;
    isError = false;

    componentDidMount() {
        this.initializeDataTable();
        this.fetchData();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.fromDate !== prevProps.fromDate || this.props.toDate !== prevProps.toDate) {
            this.fetchData();
        }
        this.updateDataTable();
    }

    componentWillUnmount() {
        if (this.dataTableInstance) {
            this.dataTableInstance?.destroy();
        }
    }

    initializeDataTable = () => {
        // @ts-ignore
        this.dataTableInstance = window.$('#reportTable').DataTable({
            orderCellsTop: true,
            fixedHeader: false,
            pageLength: 100,
            scrollCollapse: true,
            scroller: true,
            deferRender: true,
            paging: false,
            info: false,
            searching: true,
            order: [[0, 'asc']],
            bLengthChange: false,
            dom: 'Bfrtip',
            buttons: ['copy', 'csv', 'excel', 'pdf', 'print']
        });
        $('.dt-button').removeClass('dt-button buttons-html5').addClass('btn-dark btn btn-sm');
    }

    fetchData = () => {
        const { fromDate, toDate, accessTokenV2, alertsSummaryPerTraderIdMap, requestAlertsSummaryPerTraderId } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const alertsSummaryPerTraderIdInfo = alertsSummaryPerTraderIdMap[datePair];
        if (alertsSummaryPerTraderIdInfo === undefined) {
            requestAlertsSummaryPerTraderId(accessTokenV2, fromDate, toDate);
        }
        this.isLoted = true;
    }

    // ===================================== Update Data Table Info =====================================
    updateDataTable = () => {
        const { alertsSummaryPerTraderIdMap, fromDate, toDate } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const alertsSummaryPerTraderIdInfo = alertsSummaryPerTraderIdMap[datePair];
        // ----------------- If Error loading data -----------------
        if (alertsSummaryPerTraderIdInfo instanceof Error) {
            this.isError = true;
        }
        // ----------------- If data is still loading -----------------
        else if (alertsSummaryPerTraderIdInfo === 'waiting') {
            this.isLoted = true;
        }
        // ----------------- If data is loaded -----------------
        else if (this.isLoted && Array.isArray(alertsSummaryPerTraderIdInfo)) {
            const tableData = alertsSummaryPerTraderIdInfo.map((alertSummaryPerTraderId) => ({
                traderID: alertSummaryPerTraderId.trader_id,
                alertID: String(alertSummaryPerTraderId.id),
                description: alertSummaryPerTraderId.description,
                numAlerts: String(alertSummaryPerTraderId.count_alerts),
                statusCount: alertSummaryPerTraderId.status_count
            }));
            this.isLoted = false;
            this.setState({ tableData });
            return;
        }
    }

    render () {
        const { tableData } = this.state;
        const uniqueDescriptions = Array.from(new Set(tableData.map(row => row.description)));
        const statusKeys = ["forreview", "open", "watch", "investig", "critical", "explained", "closed"];

        return (
            <>
               <Row className='SecondaryNavBar'>
                    <Col xs={12} className='DatePickerRangeCol'>
                        <DatePickerRange />
                    </Col>
                </Row>
                <Row className='TableTitle'>
                    <Col xs={12} className='TableTitle-col'>
                        <h5 className='SectionTitle'>Alert Summary per Trader ID</h5> 
                        <button 
                            id='excelButton' 
                            onClick={exportToExcel} 
                            className='VerySmallButton btn btn-dark btn-sm'
                        >Excel</button>
                    </Col>
                </Row>
                <Row className='TableRow'>
                    <Col xs={12} className='TableCol'>
                        <StickyHeaderTable hideScrollbar={false}>
                            <SortFilterTable
                                data={tableData}
                                isLoading={this.isLoted}
                                isError={this.isError}
                                rowId={({ traderID }) => traderID ?? undefined}
                                columns={[
                                    {
                                        title: 'Trader ID',
                                        textAlign: 'left',
                                        dataAccessor: ({ traderID }) => traderID,
                                        simpleSortable: ({ traderID }) => traderID,
                                        filter: {
                                            id: 'Trader ID',
                                            type: 'string',
                                            stringValue: ({ traderID }) => traderID
                                        }
                                    },
                                    {
                                        title: 'Alert ID',
                                        textAlign: 'left',
                                        dataAccessor: ({ alertID }) => alertID,
                                        simpleSortable: ({ alertID }) => Number(alertID),
                                        filter: {
                                            id: 'Alert ID',
                                            type: 'string',
                                            stringValue: ({ alertID }) => alertID
                                        }
                                    },
                                    {
                                        title: 'Description',
                                        textAlign: 'left',
                                        dataAccessor: ({ description }) => description,
                                        simpleSortable: ({ description }) => description,
                                        filter: {
                                            id: 'Description',
                                            type: uniqueDescriptions,
                                            multiple: true,
                                            stringValue: ({ description }) => description
                                        }
                                    },
                                    {
                                        title: 'Number of Alerts',
                                        textAlign: 'right',
                                        dataAccessor: ({ numAlerts }) => numAlerts,
                                        simpleSortable: ({ numAlerts }) => Number(numAlerts),
                                    },
                                    {
                                        title: 'Status',
                                        textAlign: 'left',
                                        dataAccessor: ({ statusCount }) => (
                                            statusCountToString(statusCount)
                                        ),
                                        filter: {
                                            id: 'Status',
                                            type: statusKeys,
                                            multiple: true,
                                            stringValue: ({ statusCount }) => statusKeys.filter(key => Number(statusCount[key as keyof IStatusCount]) > 0).join(','),
                                        }
                                    },
                                ]}
                            />
                        </StickyHeaderTable>
                    </Col>
                </Row>
            </>
        );
    }
}

function mapStateToProps({ dashboardReducer, reportReducer, loginReducer }: RootState) {
    const { dayRange } = dashboardReducer;
    const { fromDate, toDate } = dayRange;
    const { accessTokenV2 } = loginReducer;
    const { alertsSummaryPerTraderIdMap } = reportReducer;
    return {
        fromDate,
        toDate,
        accessTokenV2,
        alertsSummaryPerTraderIdMap
    };
}

const connector = connect(mapStateToProps, { requestAlertsSummaryPerTraderId });
export default connector(ReportOfAlertSummaryPerTraderId);


